/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { FeatureFlag, isFeatureEnabled } from '../feature-flags';
import { AppState } from '@capacitor/app';
import * as dayjs from 'dayjs';
import * as _ from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GlobalConstants } from '../constant/constant';
import { ReviewPeriod } from '../entities/ReviewPeriod';
import { StoreName } from '../entities/StoreName';
import { SyncStatus } from '../entities/SyncStatus';
import { AppEventType } from '../enums/app-events.enum';
import { AppStateService } from '../services/app-state.service';
import { CurrentReviewPeriodService } from '../services/current-review-period.service';
import { EventBusService } from '../services/event-bus.service';
import { FormDefinitionsService } from '../services/form-definitions.service';
import { FormDocumentsService } from '../services/form-documents.service';
import { PhotoService } from '../services/photo.service';
import { SyncChoiceDefinitionService } from '../services/sync-choice-definition.service';
import { SyncNotificationService } from '../services/syncNotificationService';
import { UserService } from '../services/user.service';
import { DriverSessionCredentialsService } from '../services/driver-session-credentials.service';
import { DateTimeType } from '../utils/date-time-helper';
import { ProcessLockException } from '../utils/exceptions';
import { LogService } from './logging/log.service';
import { RepositoryService } from './repository.service';
import { SyncActionService } from './sync-action.service';
import { SyncDownloadService } from './sync-download.service';

export enum SyncType {
  ALL = 'all',
  APP_USER = 'app-user',
  ALL_TEAMS = 'all-teams',
  PERSONAL_TEAM = 'personal-team',
  PROJECT_TEAM = 'project-team',
  FORMS = 'forms'
}

// Type mapping for syncEntityId based on syncType
export type SyncEntityIdType<T extends SyncType> = 
  T extends SyncType.ALL ? undefined :
  T extends SyncType.PERSONAL_TEAM ? number :
  T extends SyncType.PROJECT_TEAM ? number :
  T extends SyncType.FORMS ? undefined :
  string | number | undefined;

export interface SyncOptions<T extends SyncType = SyncType> {
  syncType?: T;
  syncEntityId?: SyncEntityIdType<T>;
  startDate?: DateTimeType;
  endDate?: DateTimeType;
  fullSync?: boolean;
  waitIfProcessing?: boolean;
  ignoreThrottle?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SyncService {

  uriToken: string;
  timer: NodeJS.Timeout;
  lastSync: any;
  private onNetworkChange = new Subject();
  onNetworkChange$ = this.onNetworkChange.asObservable();
  private isConnected: boolean;
  private appStateSubscription: Subscription;
  private appState: AppState;

  constructor(
    private appStateService: AppStateService,
    private repository: RepositoryService,
    private syncDownloadService: SyncDownloadService,
    private syncNotificationService: SyncNotificationService,
    private userService: UserService,
    private driverSessionCredentialsService: DriverSessionCredentialsService,
    private syncActionService: SyncActionService,
    private formDefinitionsService: FormDefinitionsService,
    private formDocumentsService: FormDocumentsService,
    private syncChoiceDefinitionService: SyncChoiceDefinitionService,
    private photoService: PhotoService,
    private eventBusService: EventBusService,
    private currentReviewPeriodService: CurrentReviewPeriodService,
    private logService: LogService
  ) {
    this.formDocumentsService.revertInProgressDocuments();

    this.appStateSubscription = this.appStateService.getAppState().subscribe(appState => this.appState = appState);

    this.appStateService.networkStatus$.subscribe(async status => {
      this.isConnected = status.currentStatus;

      if (this.isConnected && status.previousStatus === false) {
        try {
          await this.sync();
        } catch (e) {
          if (e instanceof ProcessLockException) {
            console.log('Sync is already running.');
          } else {
            console.log('Error while trying to sync.');
          }
        }
      }
    });
  }

  startPolling() {
    let pollingTimeMs: number;
    if ('pollingTimeMs' in environment) {
      pollingTimeMs = environment.pollingTimeMs;
    } else {
      pollingTimeMs = GlobalConstants.ONE_MINUTE_MS; /* Defaults to 1 minute */
    }

    if (pollingTimeMs > 0) {
      console.log('Polling interval set to ' + pollingTimeMs + ' milliseconds.');
      if (this.timer) {
        clearInterval(this.timer);
      }
      this.timer = setInterval(async () => {
        try {
          this.sync({ 
            syncType: SyncType.ALL, 
            startDate: this.currentReviewPeriodService.value.startDate, 
            endDate: this.currentReviewPeriodService.value.endDate, 
            fullSync: false, 
            waitIfProcessing: false 
          });
        } catch (e) {
          if (e instanceof ProcessLockException) {
            console.log('Sync is already running.');
          } else {
            console.log('Error while trying to sync.');
          }
        }
      }, pollingTimeMs);
    } else {
      console.log('Polling is disabled.');
    }
  }

  stopPolling() {
    clearInterval(this.timer);
  }

  async sync({
    syncType,
    syncEntityId,
    startDate = this.currentReviewPeriodService.value.startDate,
    endDate = this.currentReviewPeriodService.value.endDate,
    fullSync = false,
    waitIfProcessing = true,
    ignoreThrottle = false
  }: SyncOptions = {}): Promise<void> {
    if (this.appState.isActive === false) {
      this.logService.debug('Sync.AppInactive');
      return;
    }

    await navigator.locks.request('sync', { ifAvailable: !waitIfProcessing }, async lock => {
      if (!lock) {
        throw new ProcessLockException('Sync is already running.');
      }
      this.logService.debug('Sync.Started', { syncType, syncEntityId, startDate, endDate, fullSync, ignoreThrottle });

      await this.syncActionService.startProcessingActionQueue();

      switch (syncType) {
        case SyncType.ALL:
          await this.syncDownloadService.syncAll(startDate, endDate, fullSync, ignoreThrottle);
          await this.syncFormData(fullSync);
          break;

        case SyncType.APP_USER:
          await this.syncDownloadService.syncAppUser(startDate, endDate, fullSync, ignoreThrottle);
          break;

        case SyncType.ALL_TEAMS:
          await this.syncDownloadService.syncAll(startDate, endDate, fullSync, ignoreThrottle);
          break;

        case SyncType.PERSONAL_TEAM:
          await this.syncDownloadService.syncTeam(syncEntityId as number, startDate, endDate, fullSync, ignoreThrottle);
          break;

        case SyncType.PROJECT_TEAM:
          await this.syncDownloadService.syncProject(syncEntityId as number, startDate, endDate, fullSync, ignoreThrottle);
          break;

        case SyncType.FORMS:
          await this.syncFormData(fullSync);
          break;

        default:
          throw new Error('Invalid sync type specified.');
      }

      await this.uploadAllPendingRecords();

      // Recover from a failed login-time rotation and catch approaching expirations on every sync.
      if (isFeatureEnabled(FeatureFlag.DriverSession)) {
        try {
          await this.driverSessionCredentialsService.ensureIssuance();
          this.logService.event('Sync.IssuanceValidated');
        } catch (error) {
          this.logService.error('Sync.IssuanceValidationFailed', error);
        }
      }

      this.eventBusService.emit({ type: AppEventType.SYNC_COMPLETE, data: null });
      this.logService.debug('Sync.Completed', { syncType, syncEntityId, startDate, endDate, fullSync, ignoreThrottle });
    });
  }

  private async syncFormData(fullSync = false): Promise<void> {
    this.logService.debug('Sync.FormData');
    await this.formDefinitionsService.sync();
  await this.syncChoiceDefinitionService.sync({ fullSync });
    await this.formDocumentsService.revertInProgressDocuments(true);
    await this.formDocumentsService.syncAll();
  }

  /**
   * @deprecated Remove after refactoring virtual badge upload process
   */
  async uploadAllPendingRecords(): Promise<void> {
    for await (const entity of GlobalConstants.uploadEntity) {
      let recordsToUpload = await this.getPendingRecordsByStore(entity.tableName);

      for await (const record of recordsToUpload) {
        await this.uploadRecord(entity.tableName, record.RecordId);
      }
    }

    return Promise.resolve();
  }

  /**
   * @deprecated Remove after refactoring virtual badge upload process
   */
  async uploadRecord(storeName: string, recordId: number): Promise<void> {
    const entity = GlobalConstants.uploadEntity.filter(e => e.tableName.toLowerCase() === storeName.toLowerCase())[0];
    let record = await this.repository.getByLocalId(entity.tableName, recordId);

    if (record && entity.url) {
      let uriPost = entity.url;
      if (record.Id && record.Id > 0 && entity.updateUrl != null) {
        uriPost = entity.updateUrl;
      }

      if (entity.tableName === StoreName.Photo) {
        this.photoService
          .uploadPhoto(record.fileName, record.dataUrl)
          .then(async token => {
            if (record.isBadgePhoto) {
              record.uriToken = token;

              if (record.syncStatus === SyncStatus.Error) {
                record.syncStatus = SyncStatus.Completed;
                try {
                  await this.userService.updateUserBadge(record);
                } catch (err) {
                  record.syncStatus = SyncStatus.Error;
                  record.uploadRetryCount = 0;

                  record.isNetworkAvailable = err.error instanceof ProgressEvent ? false : true;
                  await this.repository.create(record, entity.tableName);
                }
              }
            }

            record.syncStatus = SyncStatus.Completed;
            record.Id = 1;
            await this.repository.create(record, StoreName.Photo);

            this.syncNotificationService.emitChange({
              syncProcess: 'upload',
              Status: 'Completed',
              EntityName: entity.tableName,
              RecordId: record.RecordId,
              activityId: record.activityId,
              entityId: record.entityId,
              attachmentId: record.attachmentId,
              attachment: true,
            });
          })
          .catch(async err => {
            record.syncStatus = SyncStatus.Error;
            record.uploadRetryCount = 0;
            //isNetworkAvailable used in timecock.component.ts
            record.isNetworkAvailable = err.error instanceof ProgressEvent ? false : true;
            await this.repository.create(record, entity.tableName);
          });
      }

      // event emitter for upload completion
      this.syncNotificationService.emitChange({
        syncProcess: 'upload',
        Status: 'Completed',
        EntityName: entity.tableName,
      });
    }

    return Promise.resolve();
  }

  /**
   * @deprecated Remove after refactoring virtual badge upload process
   */
  async getPendingRecordsByStore(storeName: string): Promise<any[]> {
    const entity = GlobalConstants.uploadEntity.filter(e => e.tableName.toLowerCase() === storeName.toLowerCase())[0];
    let pendingRecords = await this.repository.getAll(entity.tableName, undefined);
    pendingRecords = pendingRecords.filter(e => e.syncStatus === SyncStatus.Error || e.syncStatus === SyncStatus.Ready);

    if (pendingRecords.length > 0 && entity.url) {
      pendingRecords = _.orderBy(pendingRecords, record => (record.CreatedDate && dayjs(record.CreatedDate)) || record.RecordId, 'asc');
    }

    return pendingRecords;
  }

  async loginSync() {
    try {
      await this.sync({ 
        syncType: SyncType.ALL, 
        startDate: this.currentReviewPeriodService.value.startDate, 
        endDate: this.currentReviewPeriodService.value.endDate, 
        fullSync: false, 
        waitIfProcessing: false,
        ignoreThrottle: true
      });
    } catch (e) {
      if (e instanceof ProcessLockException) {
        this.logService.warn('Sync.LoginDeferred');
      } else {
        this.logService.error('Sync.LoginFailed', e);
      }
    }
    await this.formDocumentsService.assignDeviceIdAndDocumentIdSequence();
    
    await this.userService.syncUserBadge();
  }

  async getCurrentUserLastSyncDate(): Promise<string> {
    const getAppUserLastSyncDate = await this.syncDownloadService.getAppUserLastSyncDate();
    return getAppUserLastSyncDate;
  }

  async checkIsInitialSyncCompleted(): Promise<boolean> {
    try {
      const previousAppUserLastSyncDate = await this.syncDownloadService.getAppUserLastSyncDate();
      if (previousAppUserLastSyncDate) {
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  }

}
